<template>
    <section :class="['catalog-readiness-dashboard', { dark: isDark }]">
        <header class="dashboard-header">
            <div>
                <p class="eyebrow">Focused dashboard</p>
                <h1>Product &amp; Category</h1>
                <p class="subtitle">A store-level readiness check for the products that are physically on hand.</p>
            </div>
            <router-link class="header-link" to="/products">All products <i class="material-icons">arrow_forward</i></router-link>
        </header>

        <section class="control-bar" aria-label="Catalog readiness filters">
            <label>
                <span>Store</span>
                <select v-model="selectedWarehouseId" :disabled="loading" @change="applyFilters">
                    <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">{{ warehouse.name }}</option>
                </select>
            </label>
            <label class="search-field">
                <span>Search products</span>
                <input v-model.trim="searchDraft" type="search" placeholder="Name, SKU, brand, category" @keyup.enter="applyFilters">
            </label>
            <label>
                <span>Issue</span>
                <select v-model="selectedIssue" @change="applyFilters">
                    <option value="">All readiness issues</option>
                    <option v-for="option in issueOptions" :key="option.code" :value="option.code">{{ option.label }}</option>
                </select>
            </label>
            <button class="apply-button" type="button" :disabled="loading || !selectedWarehouseId" @click="applyFilters">
                {{ loading ? "Checking…" : "Apply" }}
            </button>
        </section>

        <div v-if="!canViewReadiness" class="state-panel permission-state">
            <i class="material-icons">lock</i>
            <div>
                <h2>Catalog readiness access required</h2>
                <p>Product view and inventory manager permissions are both required because this view combines catalog metadata with store stock.</p>
            </div>
        </div>
        <div v-else-if="loading" class="state-panel loading-state" role="status">
            <i class="material-icons">sync</i>
            <div><h2>Checking catalog readiness</h2><p>Loading the complete in-stock population for {{ warehouseName }}…</p></div>
        </div>
        <div v-else-if="error" class="state-panel error-state" role="alert">
            <i class="material-icons">error_outline</i>
            <div>
                <h2>Readiness data could not load</h2>
                <p>{{ error }}</p>
                <button type="button" @click="fetchReadiness">Try again</button>
            </div>
        </div>
        <template v-else>
            <section class="metric-grid" aria-label="Catalog readiness summary">
                <article v-for="card in summaryCards" :key="card.label" :class="['metric-card', card.tone]">
                    <div class="metric-icon"><i class="material-icons">{{ card.icon }}</i></div>
                    <div>
                        <span>{{ card.label }}</span>
                        <strong>{{ card.value }}</strong>
                        <small>{{ card.helper }}</small>
                    </div>
                </article>
            </section>

            <section class="insight-grid">
                <article class="dashboard-card issue-chart-card">
                    <div class="section-heading">
                        <div><p class="eyebrow">Issue mix</p><h2>What blocks readiness</h2></div>
                        <span class="source-badge">Complete population</span>
                    </div>
                    <div v-if="loading" class="chart-placeholder">Loading catalog checks…</div>
                    <EChartsHorizontalBarChart
                        v-else
                        :categories="issueChartCategories"
                        :values="issueChartValues"
                        :is-dark="isDark"
                        series-name="Products"
                        aria-label="Catalog readiness issue counts"
                        color="#c9964a"
                        dark-color="#e7bd78"
                        :value-formatter="formatInteger"
                    />
                </article>

                <article class="dashboard-card definition-card">
                    <div class="section-heading"><div><p class="eyebrow">Definitions</p><h2>How readiness is measured</h2></div></div>
                    <div class="definition-row">
                        <span class="definition-marker pos"></span>
                        <div><strong>POS ready</strong><p>Positive on-hand stock, a valid selling price, and a usable identifier that does not collide with another active product.</p></div>
                    </div>
                    <div class="definition-row">
                        <span class="definition-marker storefront"></span>
                        <div><strong>Merchandising complete</strong><p>POS ready, plus an image, category, and brand. Online publication is handled separately in the Ecommerce phase.</p></div>
                    </div>
                    <div class="definition-row">
                        <span class="definition-marker source"></span>
                        <div><strong>Source coverage</strong><p>{{ source.scope || "Complete positive on-hand stock, including variants unavailable to POS" }} for {{ warehouseName }}.</p></div>
                    </div>
                </article>
            </section>

            <section class="dashboard-card action-table-card">
                <div class="section-heading table-heading">
                    <div>
                        <p class="eyebrow">Action queue</p>
                        <h2>Products needing attention</h2>
                        <p>{{ resultCountLabel }}</p>
                    </div>
                    <button v-if="selectedIssue || search" class="clear-button" type="button" @click="clearFilters">Clear filters</button>
                </div>

                <div class="table-scroll">
                    <table>
                        <thead><tr><th>Product</th><th>On hand</th><th>Price</th><th>Readiness</th><th>Issues</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="product in products" :key="product.id || product.itemCode">
                                <td>
                                    <div class="product-cell">
                                        <div class="product-thumb"><img v-if="product.image" :src="imageUrl(product.image)" alt=""><span v-else>{{ initials(product.name) }}</span></div>
                                        <div><strong>{{ product.name }}</strong><small>{{ product.itemCode }}<template v-if="product.brand"> · {{ product.brand }}</template></small></div>
                                    </div>
                                </td>
                                <td>{{ formatNumber(product.stock) }}</td>
                                <td>{{ product.price ? formatCurrency(product.price) : "—" }}</td>
                                <td><span :class="['readiness-pill', product.posReady ? 'ready' : 'blocked']">{{ product.posReady ? "POS ready" : "Blocked" }}</span></td>
                                <td><div class="issue-list"><span v-for="issue in product.issues" :key="issue" :class="['issue-pill', issueTone(issue)]">{{ issueLabel(issue) }}</span></div></td>
                                <td>
                                    <div class="remediation-links">
                                        <router-link v-if="product.parentId" class="row-link" :to="`/products/${product.parentId}`">Parent</router-link>
                                        <router-link v-if="product.id" class="row-link" :to="`/products/${product.id}`">Variant</router-link>
                                        <span v-if="!product.parentId && !product.id">—</span>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="!loading && !products.length"><td colspan="6" class="empty-row">No products match these filters.</td></tr>
                            <tr v-if="loading"><td colspan="6" class="empty-row">Checking the complete in-stock catalog…</td></tr>
                        </tbody>
                    </table>
                </div>
                <footer v-if="pagination.totalPages > 1" class="pagination-bar">
                    <button type="button" :disabled="pagination.page <= 1 || loading" @click="changePage(pagination.page - 1)">Previous</button>
                    <span>Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
                    <button type="button" :disabled="pagination.page >= pagination.totalPages || loading" @click="changePage(pagination.page + 1)">Next</button>
                </footer>
            </section>

            <p v-if="source.refreshedAt" class="refresh-note">Live from the local API and database · refreshed {{ formatTimestamp(source.refreshedAt) }}</p>
        </template>

        <AnalyticsGoalPanel :is-dark="isDark" active-goal-id="product-category" />
    </section>
</template>

<script>
import { mapActions, mapState } from "vuex"
import AnalyticsGoalPanel from "../../components/analytics/AnalyticsGoalPanel.vue"
import EChartsHorizontalBarChart from "../../components/charts/EChartsHorizontalBarChart.vue"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import { http, serverURL } from "../../utils/http"

const integerFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 })
const numberFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 3 })
const currencyFormatter = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" })

export default {
    name: "ProductCategoryDashboard",
    components: { AnalyticsGoalPanel, EChartsHorizontalBarChart },
    data() {
        return {
            loading: false,
            error: "",
            selectedWarehouseId: "",
            selectedIssue: "",
            searchDraft: "",
            search: "",
            summary: {},
            issueMix: [],
            issueOptions: [],
            products: [],
            pagination: { page: 1, limit: 25, totalResults: 0, totalPages: 0 },
            source: {},
            requestSequence: 0,
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses"]),
        ...mapState("auth", ["user"]),
        isDark() { return this.theme === this.THEMES.DARK },
        canViewReadiness() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_PRODUCT)
                && this.hasPermission(PERMISSION_CODES.CMS_VIEW_INVENTORY_MANAGER)
        },
        warehouseName() {
            return this.warehouses.find(item => item.id === this.selectedWarehouseId)?.name || "the selected store"
        },
        summaryCards() {
            return [
                { label: "In-stock products", value: this.formatInteger(this.summary.stockedProducts), helper: `${this.formatNumber(this.summary.totalUnits)} units on hand`, icon: "inventory_2", tone: "neutral" },
                { label: "POS ready", value: this.formatInteger(this.summary.posReadyProducts), helper: `${this.formatPercent(this.summary.posReadyPercent)} of stocked products`, icon: "point_of_sale", tone: this.summary.posReadyPercent === 100 ? "success" : "warning" },
                { label: "Merchandising complete", value: this.formatInteger(this.summary.merchandisingReadyProducts), helper: `${this.formatPercent(this.summary.merchandisingReadyPercent)} with image, category, and brand`, icon: "storefront", tone: this.summary.merchandisingReadyPercent === 100 ? "success" : "warning" },
                { label: "Needs attention", value: this.formatInteger(this.summary.productsWithIssues), helper: "One product can have multiple issues", icon: "rule", tone: this.summary.productsWithIssues ? "danger" : "success" },
            ]
        },
        issueChartRows() { return this.issueMix.filter(item => item.count > 0).sort((a, b) => b.count - a.count) },
        issueChartCategories() { return this.issueChartRows.map(item => item.label) },
        issueChartValues() { return this.issueChartRows.map(item => item.count) },
        resultCountLabel() {
            const total = this.pagination.totalResults || 0
            return `${this.formatInteger(total)} matching ${total === 1 ? "product" : "products"} at ${this.warehouseName}`
        },
    },
    watch: {
        warehouses: {
            immediate: true,
            handler(warehouses) {
                if (this.selectedWarehouseId || !warehouses?.length) return
                const preferred = warehouses.find(item => item.name === "TENTH LINE - H") || warehouses[0]
                this.selectedWarehouseId = preferred.id
                if (this.canViewReadiness) this.fetchReadiness()
            },
        },
    },
    mounted() { this.setData({ pageTitle: "Product & Category" }) },
    methods: {
        ...mapActions("misc", ["setData"]),
        hasPermission(permissionId) {
            return checkHasPermission({ isAdmin: Boolean(this.user?.isAdmin), permissionId, userPermissionIds: this.user?.permissionIds || [] })
        },
        async fetchReadiness(page = this.pagination.page || 1) {
            if (!this.canViewReadiness || !this.selectedWarehouseId) return
            const requestId = ++this.requestSequence
            this.loading = true
            this.error = ""
            try {
                const response = await http.get("/analytics/catalog-readiness", { params: {
                    warehouse_id: this.selectedWarehouseId,
                    issue: this.selectedIssue || undefined,
                    search: this.search || undefined,
                    page,
                    limit: this.pagination.limit,
                } })
                if (requestId !== this.requestSequence) return
                const result = response.data.result || {}
                this.summary = result.summary || {}
                this.issueMix = result.issueMix || []
                this.issueOptions = result.issueOptions || []
                this.products = result.products || []
                this.pagination = result.pagination || this.pagination
                this.source = result.source || {}
            } catch (error) {
                if (requestId !== this.requestSequence) return
                this.error = error.response?.data?.message || "The catalog readiness source returned an error."
                this.products = []
            } finally {
                if (requestId === this.requestSequence) this.loading = false
            }
        },
        applyFilters() { this.search = this.searchDraft; this.fetchReadiness(1) },
        clearFilters() { this.searchDraft = ""; this.search = ""; this.selectedIssue = ""; this.fetchReadiness(1) },
        changePage(page) { this.fetchReadiness(page) },
        issueLabel(code) { return this.issueOptions.find(item => item.code === code)?.label || code.replace(/_/g, " ") },
        issueTone(code) { return this.issueOptions.find(item => item.code === code)?.severity || "warning" },
        formatInteger(value) { return integerFormatter.format(Number(value) || 0) },
        formatNumber(value) { return numberFormatter.format(Number(value) || 0) },
        formatPercent(value) { return `${Number(value || 0).toFixed(1)}%` },
        formatCurrency(value) { return currencyFormatter.format(Number(value) || 0) },
        formatTimestamp(value) { return new Intl.DateTimeFormat("en-CA", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) },
        initials(name) { return `${name || "?"}`.split(/\s+/).slice(0, 2).map(word => word[0]).join("").toUpperCase() },
        imageUrl(path) { return /^https?:\/\//.test(path) ? path : `${serverURL}${path.startsWith("/") ? "" : "/"}${path}` },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.catalog-readiness-dashboard { padding: 18px 24px 40px; color: $textColor; }
.catalog-readiness-dashboard.dark { color: $darkTextColor; }
.dashboard-header, .section-heading, .table-heading, .pagination-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.dashboard-header { margin-bottom: 20px; }
h1 { color: $primaryColor; font-size: 30px; line-height: 1.1; margin: 0 0 6px; }
.dark h1, .dark h2 { color: $secondaryColorDark; }
.subtitle, .section-heading p, .definition-row p { color: rgba($textColor, .66); margin: 0; }
.dark .subtitle, .dark .section-heading p, .dark .definition-row p { color: rgba($darkTextColor, .64); }
.eyebrow { color: rgba($textColor, .55); font-size: 11px; font-weight: 800; letter-spacing: .08em; margin: 0 0 4px; text-transform: uppercase; }
.dark .eyebrow { color: rgba($darkTextColor, .58); }
.header-link, .row-link { align-items: center; color: $primaryColor; display: inline-flex; font-weight: 700; gap: 6px; text-decoration: none; }
.dark .header-link, .dark .row-link { color: $secondaryColorDark; }
.control-bar, .dashboard-card, .state-panel { background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: 8px; box-shadow: $cardShadow; }
.dark .control-bar, .dark .dashboard-card, .dark .state-panel { background: $darkCardBg; border-color: $darkBorderColor; }
.control-bar { align-items: end; display: grid; gap: 12px; grid-template-columns: minmax(190px, 1fr) minmax(260px, 1.6fr) minmax(190px, 1fr) auto; margin-bottom: 18px; padding: 14px; }
label span { display: block; font-size: 11px; font-weight: 800; margin: 0 0 6px; text-transform: uppercase; }
select, input { background: transparent; border: 1px solid rgba($textColor, .2); border-radius: 6px; color: inherit; min-height: 42px; padding: 8px 10px; width: 100%; }
.dark select, .dark input { border-color: rgba($darkTextColor, .2); }
.apply-button, .clear-button, .pagination-bar button, .error-state button { border: 0; border-radius: 6px; cursor: pointer; font-weight: 700; min-height: 42px; padding: 9px 17px; }
.apply-button { background: $primaryColor; color: #fff; }
.apply-button:disabled, .pagination-bar button:disabled { cursor: default; opacity: .45; }
.metric-grid { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 18px; }
.metric-card { align-items: flex-start; background: $lightCardBg; border: 1px solid $lightBorderColor; border-left: 4px solid rgba($textColor, .2); border-radius: 8px; box-shadow: $cardShadow; display: flex; gap: 13px; min-height: 126px; padding: 18px; }
.dark .metric-card { background: $darkCardBg; border-color: $darkBorderColor; }
.metric-card.success { border-left-color: $successColor; } .metric-card.warning { border-left-color: $warningColor; } .metric-card.danger { border-left-color: $dangerColor; }
.metric-icon { align-items: center; background: rgba($primaryColor, .08); border-radius: 8px; color: $primaryColor; display: flex; height: 42px; justify-content: center; width: 42px; }
.metric-card span, .metric-card small { color: rgba($textColor, .62); display: block; }
.dark .metric-card span, .dark .metric-card small { color: rgba($darkTextColor, .62); }
.metric-card strong { display: block; font-size: 28px; line-height: 1.2; margin: 2px 0 4px; }
.insight-grid { display: grid; gap: 18px; grid-template-columns: minmax(0, 1.45fr) minmax(320px, .8fr); margin-bottom: 18px; }
.dashboard-card { padding: 18px; }
.section-heading { margin-bottom: 14px; }
.section-heading h2 { color: $primaryColor; font-size: 19px; margin: 0; }
.source-badge, .readiness-pill, .issue-pill { border-radius: 999px; display: inline-block; font-size: 10px; font-weight: 800; padding: 5px 9px; text-transform: uppercase; }
.source-badge { background: rgba($primaryColor, .08); color: $primaryColor; }
.issue-chart-card .echarts-horizontal-bar-chart, .chart-placeholder { height: 310px; }
.chart-placeholder { align-items: center; color: rgba($textColor, .55); display: flex; justify-content: center; }
.definition-row { align-items: flex-start; border-top: 1px solid rgba($textColor, .1); display: flex; gap: 12px; padding: 16px 0; }
.definition-row:first-of-type { border-top: 0; }
.definition-row p { font-size: 13px; margin-top: 3px; }
.definition-marker { border-radius: 50%; flex: 0 0 10px; height: 10px; margin-top: 5px; }
.definition-marker.pos { background: $successColor; } .definition-marker.storefront { background: $warningColor; } .definition-marker.source { background: $primaryColor; }
.action-table-card { padding: 0; overflow: hidden; }
.table-heading { padding: 18px; }
.clear-button, .pagination-bar button, .error-state button { background: rgba($primaryColor, .08); color: $primaryColor; }
.table-scroll { overflow-x: auto; }
table { border-collapse: collapse; width: 100%; }
th { background: rgba($primaryColor, .04); color: rgba($textColor, .62); font-size: 11px; letter-spacing: .04em; padding: 11px 14px; text-align: left; text-transform: uppercase; }
td { border-top: 1px solid rgba($textColor, .09); padding: 13px 14px; vertical-align: middle; }
.dark th { background: rgba($darkTextColor, .04); color: rgba($darkTextColor, .62); } .dark td { border-color: rgba($darkTextColor, .09); }
.product-cell { align-items: center; display: flex; gap: 10px; min-width: 260px; }
.product-cell small { color: rgba($textColor, .55); display: block; margin-top: 2px; }
.dark .product-cell small { color: rgba($darkTextColor, .55); }
.product-thumb { align-items: center; background: rgba($primaryColor, .08); border-radius: 7px; color: $primaryColor; display: flex; flex: 0 0 42px; font-size: 12px; font-weight: 800; height: 42px; justify-content: center; overflow: hidden; }
.product-thumb img { height: 100%; object-fit: cover; width: 100%; }
.readiness-pill.ready { background: rgba($successColor, .14); color: darken($successColor, 12%); } .readiness-pill.blocked { background: rgba($dangerColor, .12); color: $dangerColor; }
.issue-list { display: flex; flex-wrap: wrap; gap: 5px; min-width: 240px; }
.remediation-links { display: flex; gap: 9px; }
.issue-pill.critical { background: rgba($dangerColor, .1); color: $dangerColor; } .issue-pill.warning { background: rgba($warningColor, .15); color: darken($warningColor, 18%); }
.empty-row { color: rgba($textColor, .58); padding: 34px; text-align: center; }
.pagination-bar { border-top: 1px solid rgba($textColor, .1); padding: 13px 18px; }
.state-panel { align-items: center; display: flex; gap: 15px; margin-top: 18px; padding: 24px; }
.state-panel h2 { color: $primaryColor; margin: 0 0 4px; } .state-panel p { margin: 0; }
.state-panel > i { color: $warningColor; font-size: 34px; }
.refresh-note { color: rgba($textColor, .5); font-size: 11px; margin: 12px 2px 0; text-align: right; }
@media (max-width: 1100px) { .metric-grid { grid-template-columns: repeat(2, 1fr); } .control-bar { grid-template-columns: 1fr 1fr; } .insight-grid { grid-template-columns: 1fr; } }
@media (max-width: 650px) { .catalog-readiness-dashboard { padding: 14px; } .dashboard-header { align-items: flex-start; flex-direction: column; } .control-bar, .metric-grid { grid-template-columns: 1fr; } }
</style>
